/**
 * @file
 * The popover toolbar: declarative options or fluent builders.
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { components } = berTags;
  const { el, button, onClick, setHidden } = berTags.dom;

  components.toolbar = ({
    header,
    id,
    search = null,
    toggles = [],
    sort = null,
    filter = null,
    store = null,
    labelBubbles = true,
  } = {}) => {
    const api = {
      search: null,
      sortBar: null,
      toggles: {},
      tray: null,
      filter: null,
    };
    let viewBar = null;
    // The opener hides while every control in the tray is hidden (e.g.
    // only a filter with nothing to filter by). The observer keeps it in
    // step with dynamic hiding, third-party buttons included.
    const syncTray = () => {
      if (!api.tray) {
        return;
      }
      const hasControls = !!api.tray.el.querySelector('button:not(.is-hidden)');
      setHidden(api.tray.toggle, !hasControls);
      // The collapsed tray still counts as a flex item and would keep the
      // header's gap around a zero-width box - drop it entirely.
      setHidden(api.tray.el, !hasControls);
    };
    const ensureTray = () => {
      if (!api.tray) {
        api.tray = components.tray({
          id: `${id || 'ber-toolbar'}-tools`,
          showLabel: Drupal.t('Show toolbar'),
          hideLabel: Drupal.t('Hide toolbar'),
          store,
          // Collapsing the toolbar folds away button-owned panels too.
          onToggle: (open) => {
            if (!open && api.filter) {
              api.filter.closePanel();
            }
          },
        });
        header.append(api.tray.el, api.tray.toggle);
        new MutationObserver(syncTray).observe(api.tray.el, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        syncTray();
      }
      return api.tray;
    };
    const ensureViewBar = () => {
      if (!viewBar) {
        viewBar = el('div', 'ber-tags__view');
        // The toggle group sits before the sort pills, whatever the order
        // of the builder calls.
        ensureTray().el.insertBefore(
          viewBar,
          api.sortBar ? api.sortBar.el : null,
        );
      }
      return viewBar;
    };
    api.addSearch = (options) => {
      api.search = components.searchBox(options);
      header.insertBefore(api.search, header.firstChild);
      return api;
    };
    api.addToggle = (options) => {
      const toggle = components.viewToggle(options);
      api.toggles[options.key] = toggle;
      ensureViewBar().appendChild(toggle.el);
      return api;
    };
    api.addSort = (options) => {
      api.sortBar = components.sortBar(options);
      ensureTray().el.appendChild(api.sortBar.el);
      return api;
    };
    api.addButton = (options) => {
      const pill = button('ber-tags__sort-option', options.label || '');
      if (options.classes) {
        pill.classList.add(...options.classes.split(/\s+/).filter(Boolean));
      }
      if (options.label) {
        pill.textContent = options.label;
      }
      if (options.onClick) {
        onClick(pill, options.onClick);
      }
      // Alterable, like a Drupal alter hook: a bubbling event with a mutable
      // detail lets any module reweight (or restyle) the button before it is
      // placed - e.g. to reorder buttons another module added.
      const context = {
        label: options.label || '',
        classes: options.classes || '',
        weight: Number(options.weight) || 0,
        el: pill,
        toolbar: api,
      };
      document.dispatchEvent(
        new CustomEvent('ber-ui:toolbar-button', {
          detail: context,
        }),
      );
      // Order buttons by the final weight (default 0), lightest first, stable
      // for equal weights - so call order does not decide their position.
      const weight = Number(context.weight) || 0;
      pill.dataset.berWeight = String(weight);
      const tray = ensureTray().el;
      const after = [
        ...tray.querySelectorAll(':scope > [data-ber-weight]'),
      ].find((sibling) => Number(sibling.dataset.berWeight) > weight);
      tray.insertBefore(pill, after || null);
      return api;
    };
    api.addFilter = (options) => {
      api.filter = components.filterBar({
        id: id || 'ber-toolbar',
        store,
        ...options,
      });
      // The toggle rides in the tray with the other tools, the selects
      // panel wraps onto its own full-width header row.
      ensureTray().el.appendChild(api.filter.toggle);
      header.appendChild(api.filter.panel);
      return api;
    };
    if (search) {
      api.addSearch(search);
    }
    toggles.forEach((toggle) => api.addToggle(toggle));
    if (sort) {
      api.addSort(sort);
    }
    if (filter) {
      api.addFilter(filter);
    }
    api.syncTray = syncTray;
    if (labelBubbles) {
      berTags.tooltip.attachText(header, {
        selector: '.ber-tags__sort-option',
        text: (control) => control.getAttribute('aria-label') || '',
      });
    }
    return api;
  };
})(Drupal);
